/* eslint-disable operator-linebreak */
import { useEffect, useRef } from 'react';

import { css, cx } from '@emotion/css';
import { observer } from 'mobx-react-lite';

import { SPACING, COLORS2 } from '^/constants/commonStyles';

import { CarListingSearchBoxViewModel } from '../../../base/desktop/viewmodel/CarListingSearchBox.viewmodel';
import { SearchBoxSteps } from '../../../base/desktop/viewmodel/SearchBoxStepManager';
import { MRTCarListingSearchBoxDesktopContentsArea } from '../contentsArea/MRTCarListingSearchBox.desktop.contentsArea';

import { MRTCitySearchInputButton } from './MRTCitySearchInputButton';
import { MRTScheduleInputButton } from './MRTScheduleInputButton';
import { MRTBirthDayInputMobile } from './MyRealTripBirthDayInput.mobile';
import { getMrtOfficeDisplayName } from '../../common/mrtOfficeDisplayName';
import { hasBlockedDifferentReturnCity } from '../../common/differentReturnGuard';

export const MRTPickupReturnSameInputsDesktop: React.FC<{
  viewmodel: CarListingSearchBoxViewModel;
  smaller?: boolean;
}> = observer(function PickupReturnSameInputsDesktop({ viewmodel, smaller }) {
  const { stepManager } = viewmodel;

  const pickupCityInputRef = useRef<HTMLInputElement>(null);
  const hasDifferentPickupReturnLocations = viewmodel.i.hasDifferentPickupReturnLocations.value;
  const pickupOfficeName = getMrtOfficeDisplayName(viewmodel.pickupLocation);
  const returnOfficeName = getMrtOfficeDisplayName(viewmodel.returnLocation);

  useEffect(() => {
    if (
      stepManager.currentStep?.type === '1_rental_city' &&
      stepManager.currentStep.autofocus === 'CitySearchInput'
    ) {
      pickupCityInputRef.current?.focus();
    }
  }, [stepManager.currentStep?.type]);

  const stepManagerStepChangeOptions = {
    afterStepChange(newStep: SearchBoxSteps) {
      if (newStep.type === '1_rental_city') {
        viewmodel.i.pickupCitySearch.searchInput.setIsFocused(true);
        pickupCityInputRef.current?.focus();
      }
    },
  } as const;

  return (
    <form
      className={cx(
        css`
          width: 100%;
          background-color: ${COLORS2.main.MrtWhite};
          border-radius: 10px;
          padding: ${SPACING.spacing24};
          &:focus-visible {
            outline: none;
          }
        `,
      )}
      onSubmit={async (e) => {
        if (viewmodel.submitActionVM.status === 'success') {
          viewmodel.submitActionVM.value.submit();
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }
        e.preventDefault();
        return false;
      }}
    >
      {/* 다른 장소 반납  */}
      {!smaller && (
        <button
          type="button"
          className={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: ${SPACING.spacing8};

            margin-bottom: 16px;
            width: fit-content;
            color: #434343;

            &:hover {
              cursor: pointer;
            }
          `}
          onClick={() => {
            if (!hasDifferentPickupReturnLocations) {
              if (
                hasBlockedDifferentReturnCity(viewmodel.i.pickupCityCode.value) ||
                hasBlockedDifferentReturnCity(viewmodel.i.tempPickupCityCode.value) ||
                hasBlockedDifferentReturnCity(viewmodel.i.returnCityCode.value) ||
                hasBlockedDifferentReturnCity(viewmodel.i.tempReturnCityCode.value)
              ) {
                viewmodel.showDifferentLocationAlert.set(true);
                return;
              }
            }

            viewmodel.i.returnCitySearch.searchInput.set('');
            viewmodel.i.returnCitySearch.selectedCityCode.set(null);
            viewmodel.i.hasDifferentPickupReturnLocations.set(!hasDifferentPickupReturnLocations);
          }}
        >
          <img
            alt="rental-location-different"
            src={
              hasDifferentPickupReturnLocations
                ? 'ic_checkbox/checkedbox.svg'
                : 'ic_checkbox/uncheckedbox.svg'
            }
          />
          다른 곳에서 반납하고 싶어요.
        </button>
      )}
      <div
        className={css`
          display: grid;
          grid-template-columns: minmax(0, 299px) minmax(0, 438px) minmax(0, 161px) 90px;
          justify-content: space-between;
          align-items: end;
          gap: 8px;
        `}
      >
        {/* 대여 & 반납 도시 검색 */}
        <MRTCitySearchInputButton
          stepId="1_rental_city"
          placeholder="어디로 떠나시나요?"
          inputRef={pickupCityInputRef}
          viewmodel={viewmodel.i.pickupCitySearch}
          title="대여/반납 장소"
          showSuggestions={stepManager.currentStep?.type === '1_rental_city_search'}
          onTyping={() => {
            if (viewmodel.stepManager.currentStep?.type !== '1_rental_city_search') {
              viewmodel.stepManager.replaceStepWithOptions(
                {
                  type: '1_rental_city_search',
                  autofocus: 'CitySearchInput',
                },
                stepManagerStepChangeOptions,
              );
            }
          }}
          onSelectCity={(cityCode, cityKoName) => {
            viewmodel.i.tempPickupCitySearch.selectedCityCode.set(cityCode);
            viewmodel.i.tempPickupCitySearch.tempSearchInput.set(cityKoName || '');
            viewmodel.i.pickupCitySearch.searchInput.set('');
            viewmodel.i.tempPickupCitySearch.searchInput.set('');
            viewmodel.i.tempPickupLocationId.set(null);
            viewmodel.i.pickupLocationId.set(null);
            stepManager.replaceStepWithOptions(
              {
                type: '2_rental_office',
              },
              stepManagerStepChangeOptions,
            );
          }}
          onClickCitySearchInputBtn={() => {
            pickupCityInputRef.current?.focus();
            if (viewmodel.i.pickupCityCode) {
              viewmodel.i.pickupCitySearch.showAutosuggestList.set(false);
            }
            if (stepManager.currentStep?.type !== '1_rental_city') {
              stepManager.replaceStepWithOptions(
                {
                  type: '1_rental_city',
                  autofocus: 'CitySearchInput',
                },
                stepManagerStepChangeOptions,
              );
            }

            if (viewmodel.pickupLocation?.location_id) {
              stepManager.replaceStepWithOptions(
                {
                  type: '2_rental_office',
                  // autofocus: 'CitySearchInput',
                },
                stepManagerStepChangeOptions,
              );
            }
          }}
          office={pickupOfficeName}
        />
        {/* 일정 선택 */}
        <MRTScheduleInputButton
          placeholder="언제 떠나시나요?"
          placeholderIcon="calendar.svg"
          viewmodel={viewmodel}
          title="대여/반납 일시"
          onClick={() => {
            stepManager.replaceStepWithOptions(
              {
                type: '3_schedule',
              },
              stepManagerStepChangeOptions,
            );
          }}
          disabled={
            hasDifferentPickupReturnLocations
              ? !viewmodel.i.pickupCitySearch.searchInput.value ||
                !pickupOfficeName ||
                !viewmodel.i.returnCitySearch.searchInput.value ||
                !returnOfficeName
              : !viewmodel.i.pickupCitySearch.searchInput.value || !pickupOfficeName
          }
          isSelected={stepManager.currentStep?.type === '3_schedule'}
          value={viewmodel.i.calendarStateViewModel.selection.start}
          valueEnd={viewmodel.i.calendarStateViewModel.selection.end}
          type="pickupReturn"
        />

        {/* 운전자 생년월일 */}
        <MRTBirthDayInputMobile viewmodel={viewmodel} />

        {/* 검색 버튼 */}
        <button
          type="submit"
          disabled={viewmodel.submitActionVM.status !== 'success'}
          className={css`
            display: flex;
            justify-content: center;
            align-items: center;
            align-self: flex-end;

            border-radius: 8px;
            background-color: ${COLORS2.main.MrtBlue1} !important;
            color: #ffffff;
            width: 90px;
            height: ${SPACING.spacing48};
            padding: 0 ${SPACING.spacing16};

            &:disabled {
              background-color: ${COLORS2.main.MrtBlue2} !important;
              cursor: not-allowed;
            }
          `}
        >
          검색
        </button>
        <MRTCarListingSearchBoxDesktopContentsArea viewmodel={viewmodel} />
      </div>
    </form>
  );
});
